import React, { type FC } from 'react'

import styled from '@emotion/styled'

import { useIntl } from 'react-intl'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  CHECKBOX_LABEL_VARIANT_BOLD,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SPACING_4,
  SpacingVertical,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  Text,
  Toggle,
} from 'mailup-web-components'

import { evalCondition } from 'mailup-react-helpers'

import Divider from 'Components/Divider'

import InputLists from 'Pages/RecipientsList/components/modals/unsubscribe/InputLists'

import {
  modalUnsubscribeCancelCallback,
  modalUnsubscribeConfirmCallback,
  modalUnsubscribeToggleFieldCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modals'
import {
  modalUnsubscribeCanUnsubscribeState,
  modalUnsubscribeState,
} from 'Pages/RecipientsList/recoil/state/modals'
import { gainsightTrack } from 'mailup-react-helpers'

const StyledToggleLabel = styled.label`
  cursor: pointer;
`

const ModalUnsubscribe: FC = () => {
  const intl = useIntl()

  const { show, channelEmail, channelSMS, wipeData } = useRecoilValue(
    modalUnsubscribeState,
  )

  const canUnsubscribe = useRecoilValue(modalUnsubscribeCanUnsubscribeState)

  const handleCancel = useRecoilCallback(modalUnsubscribeCancelCallback, [])
  const handleConfirm = useRecoilCallback(modalUnsubscribeConfirmCallback, [])

  const modalUnsubscribeToggleField = useRecoilCallback(
    modalUnsubscribeToggleFieldCallback,
    [],
  )

  const hasMultiLists: boolean = evalCondition('co:ADVRECIPIENTMGMT')

  const handleToggleField =
    (field: 'channelEmail' | 'channelSMS' | 'wipeData' | 'allLists') =>
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      if (field === 'wipeData') {
        gainsightTrack('mup_recipients_click_modalUnsubscribeOblivionToggle', {
          commercialOfferName: 'mup',
        })
      }
      if (field === 'channelEmail') {
        gainsightTrack(
          'mup_recipients_click_modalUnsubscribeEmailChannelChecked',
          {
            commercialOfferName: 'mup',
          },
        )
      }
      if (field === 'channelEmail') {
        gainsightTrack(
          'mup_recipients_click_modalUnsubscribeSmsChannelChecked',
          {
            commercialOfferName: 'mup',
          },
        )
      }
      void modalUnsubscribeToggleField(field)
    }

  return (
    <Modal
      show={show}
      onClose={handleCancel}
      data-mu-test="modal-unsubscribe"
      closeOnOverlayClick
      showClose
    >
      <ModalHead
        title={intl.formatMessage({
          id: 'Load.RecipientsList.ModalUnsubscribeTitle',
        })}
      />

      <ModalBody>
        <Flex flexDirection="column" gap="24px">
          {hasMultiLists && <InputLists />}

          <Flex flexDirection="column" gap="16px">
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: 'Load.RecipientsList.ModalUnsubscribeIntro',
              })}
              {' *'}
            </Text>

            <Checkbox
              value="email"
              checked={channelEmail}
              onChange={handleToggleField('channelEmail')}
              label={intl.formatMessage({
                id: 'Load.RecipientsList.ModalUnsubscribeChannelEmail',
              })}
              labelVariant={CHECKBOX_LABEL_VARIANT_BOLD}
              data-mu-test="channel-email"
            />
            <Checkbox
              value="sms"
              checked={channelSMS}
              onChange={handleToggleField('channelSMS')}
              label={intl.formatMessage({
                id: 'Load.RecipientsList.ModalUnsubscribeChannelSMS',
              })}
              labelVariant={CHECKBOX_LABEL_VARIANT_BOLD}
              data-mu-test="channel-sms"
            />
          </Flex>
          <Divider margin="0" />
          <section>
            <Flex flexDirection="row" alignItems="center" gap="8px">
              <Toggle
                data-mu-test="toggle-wipe-data"
                variant="sm"
                checked={wipeData}
                onChange={handleToggleField('wipeData')}
                id="toggle-wipe-data"
              />

              <StyledToggleLabel htmlFor="toggle-wipe-data">
                {intl.formatMessage({
                  id: 'Load.RecipientsList.ModalUnsubscribeWipeData',
                })}
              </StyledToggleLabel>
            </Flex>

            <SpacingVertical variant={SPACING_4} />

            <Flex flexDirection="row" gap="45px">
              <i />
              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({
                  id: 'Load.RecipientsList.ModalUnsubscribeWipeDataDescription',
                })}
              </Text>
            </Flex>
          </section>

          <Divider margin="0" />
        </Flex>
      </ModalBody>
      <ModalFoot>
        <Button
          kind={BUTTON_VARIANT_SECONDARY}
          data-mu-test="cancel"
          onClick={handleCancel}
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        </Button>
        <Button
          kind={BUTTON_VARIANT_PRIMARY}
          data-mu-test="confirm"
          onClick={handleConfirm}
          disabled={!canUnsubscribe}
        >
          {intl.formatMessage({ id: 'Load.RecipientsList.ActionUnsubscribe' })}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

export default ModalUnsubscribe
